import { useState, type FC } from 'react'

import { MessagePopup, Textarea } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'

import queries from './queries'
import terminationResources from './resources'

type Props = {
  id: number
  isOpen: boolean
  onClose: () => void
}

export const CancelTerminationModal: FC<Props> = ({ isOpen, onClose, id }) => {
  const [comment, setComment] = useState('')
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (comment: string) => terminationResources.cancelTerminationOrder(id, comment),
    {
      onSuccess: () => {
        queryClient.refetchQueries(queries.termination(id))
        queryClient.refetchQueries(queries.terminationList())

        onClose()
      },
    }
  )

  const onCloseHandler = () => {
    setComment('')
    onClose()
  }

  return (
    <MessagePopup
      title={<Translation id="Cancel termination order" />}
      message={<Translation id="Are you sure you want to cancel the termination?" />}
      isOpen={isOpen && Boolean(id)}
      onClose={onCloseHandler}
    >
      <div className="flex w-full flex-col gap-4">
        <Translation id="If yes, please provide a comment:" />
        <Textarea id="comment" value={comment} onChange={e => setComment(e.target.value)} />
      </div>
      <footer className="mt-4 flex justify-end">
        <Button variant="tertiary" onClick={onCloseHandler}>
          <Translation id="Cancel" />
        </Button>

        <Button
          variant="primary"
          isLoading={mutation.isLoading}
          onClick={() => {
            if (!comment || !id) return
            mutation.mutate(comment)
          }}
          disabled={comment.length === 0}
        >
          <Translation id="Confirm" />
        </Button>
      </footer>
    </MessagePopup>
  )
}
